import React, { useEffect, useState } from 'react';
import {
  AERIAL_TANSFER_TYPE_ENUM,
  BPT_TANSFER_TYPE_ENUM,
  REQUEST_TYPE_ENUM,
  TRANSFER_TYPE,
  TRANSFER_TYPE_ENUM
} from '../../Constants/Constants';
import { fetchApiData, interpolate } from '../../Utils/ApiCalls';
import { BULK_DUPLICATE, CHANGE_OWNER, FMS_UPLOAD, TRANSFER_REQUEST } from '../../urls';
import useFormStatus from '../../Hooks/useFormStatus';
import LoaderCircle from '../LoaderCircle';
import { Button, Input, Checkbox, RadioGroup, Select, Typography, Tooltip } from '@attentive-platform/stem-ui';

const CopyTransfer = () => {
  const [requestType, setRequestType] = useState(REQUEST_TYPE_ENUM.AERIAL);
  const [bptTransferType, setBptTransferType] = useState();
  const [aerialTransferType, setAerialTransferType] = useState();
  const [requestIds, setRequestIds] = useState([]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [shareIds, setShareIds] = useState([]);

  const [isFmsTransfer, setIsFmsTransfer] = useState(false);
  const [transferType, setTransferType] = useState(TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER);
  const [featureReportId, setFeatureReportId] = useState();
  const [portfolioName, setPortfolioName] = useState();
  const [folderName, setFolderName] = useState();
  const [perAcreCost, setPerAcreCost] = useState();
  const [folderIds, setFolderIds] = useState();
  const [retry, setRetry] = useState(false);
  const [remeasurementRequest, setRemeasurementRequest] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTakeoffFT, setIsTakeoffFT] = useState(false);
  const [copyTakeoffForCurrentUser, setCopyTakeoffForCurrentUser] = useState(false);

  const { successMessage, errorMessage, handleApiResult } = useFormStatus();

  useEffect(() => {
    if (
      aerialTransferType !== AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE ||
      transferType === TRANSFER_TYPE_ENUM.NON_PORTFOLIO
    )
      setIsFmsTransfer(false);
  }, [transferType, isFmsTransfer, aerialTransferType]);

  useEffect(() => {
    clearAllFields(false);
  }, [requestType]);

  const isBPT = requestType === REQUEST_TYPE_ENUM.CONSTRUCTION;

  const clearAllFields = (apiCall = true) => {
    apiCall && setRequestType(undefined);
    setBptTransferType(undefined);
    setAerialTransferType(undefined);
    setRequestIds([]);
    setIsFmsTransfer(false);
    setTransferType(TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER);
    setFeatureReportId(undefined);
    setPortfolioName(undefined);
    setFolderName(undefined);
    setPerAcreCost(undefined);
    setFolderIds([]);
    setRetry(false);
    setRemeasurementRequest(false);
    setEmail('');
    setCustomerEmail('');
    setCopyTakeoffForCurrentUser(false);
    setIsTakeoffFT(false);
    setShareIds([]);
  };

  const handleFmsToFalconTransfer = async () => {
    let requestIdsList = requestIds;
    if (typeof requestIds === 'string') {
      requestIdsList = cleanEnteredValues(requestIds);
    }
    const data = {
      ids: requestIdsList,
      user_email: customerEmail.toLowerCase(),
      portfolio_name: portfolioName,
      feature_report: featureReportId,
      folder_name: folderName,
      retry: retry,
      per_acre_cost: perAcreCost,
      is_remeasurement_request: remeasurementRequest
    };
    setLoading(true);
    const response = await fetchApiData({
      endpoint: FMS_UPLOAD,
      data: data
    });
    handleApiResult(response);

    setLoading(false);

    if (response.error === null) clearAllFields();
  };

  const handleNotCopyAndCharge = async ({ isPortfolioTransfer = false, isBPT }) => {
    let requestIdsList = requestIds;
    let shareIdsList = shareIds;
    if (typeof requestIds === 'string') {
      requestIdsList = cleanEnteredValues(requestIds);
    }
    if (typeof shareIds === 'string') {
      shareIdsList = cleanEnteredValues(shareIds);
    }
    const data = {
      request_ids: requestIdsList,
      user_email: customerEmail.toLowerCase(),
      per_acre_cost: perAcreCost,
      is_remeasurement_request: remeasurementRequest
    };
    if (isPortfolioTransfer) {
      data.portfolio_name = portfolioName;
      data.folder_name = folderName;
    }
    if (isBPT) {
      data.copy_takeoff_for_current_user = copyTakeoffForCurrentUser;
      data.is_takeoff_ft = isTakeoffFT;
      data.share_ids = shareIdsList;
    }
    setLoading(true);
    const response = await fetchApiData({
      endpoint: interpolate(TRANSFER_REQUEST, [isBPT ? '/dashboard' : '/admin/requests']),

      data: data
    });
    handleApiResult(response);

    setLoading(false);

    if (response.error === null) clearAllFields();
  };

  const handleCopyAndNotCharge = async ({ isBPT }) => {
    let requestIdsList = requestIds;
    let shareIdsList = shareIds;
    let folderIdsList = folderIds;
    if (typeof requestIds === 'string') {
      requestIdsList = cleanEnteredValues(requestIds);
    }
    if (typeof folderIds === 'string') {
      folderIdsList = cleanEnteredValues(folderIds);
    }
    if (typeof shareIds === 'string') {
      shareIdsList = cleanEnteredValues(shareIds);
    }
    const data = {
      request_ids: requestIdsList,
      user_email: customerEmail.toLowerCase(),
      folder_ids: folderIdsList,
      charge_user: false
    };
    if (isBPT) {
      data.share_ids = shareIdsList;
    }
    setLoading(true);
    const response = await fetchApiData({
      endpoint: interpolate(BULK_DUPLICATE, [isBPT ? '/dashboard' : '/admin/requests']),
      data: data
    });
    handleApiResult(response);

    setLoading(false);

    if (response.error === null) clearAllFields();
  };

  const handleNotCopyAndNotCharge = async ({ isBPT }) => {
    let requestIdsList = requestIds;
    if (typeof requestIds === 'string') {
      requestIdsList = cleanEnteredValues(requestIds);
    }

    const data = {
      request_ids: requestIdsList,
      email: email
    };
    setLoading(true);
    const response = await fetchApiData({
      endpoint: interpolate(CHANGE_OWNER, [isBPT ? '/dashboard' : '/admin/requests']),
      data: data
    });
    handleApiResult(response);
    setLoading(false);
    if (response.error === null) clearAllFields();
  };

  const cleanEnteredValues = ids => {
    const cleanedValues = ids
      .split(',')
      .map(value => value.trim())
      .filter(value => value !== '');

    const cleanedString = cleanedValues.join(',');
    const individualValues = cleanedString.split(',');
    return individualValues;
  };

  const handleTransfer = () => {
    if (typeof requestIds === 'string') {
      cleanEnteredValues(requestIds);
    }
    if (typeof folderIds === 'string') {
      cleanEnteredValues(folderIds);
    }

    if (requestType === REQUEST_TYPE_ENUM.AERIAL) {
      if (aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE) {
        if (transferType === TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER) {
          if (isFmsTransfer) {
            handleFmsToFalconTransfer();
          } else {
            handleNotCopyAndCharge({ isPortfolioTransfer: true });
          }
        } else {
          handleNotCopyAndCharge({});
        }
      } else if (aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE) {
        handleCopyAndNotCharge({});
      } else if (aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE) {
        handleNotCopyAndNotCharge({});
      }
    } else if (requestType === REQUEST_TYPE_ENUM.CONSTRUCTION) {
      if (bptTransferType === BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE) {
        handleNotCopyAndCharge({ isBPT });
      } else if (bptTransferType === BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE) {
        handleCopyAndNotCharge({ isBPT });
      } else if (bptTransferType === BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE) {
        handleNotCopyAndNotCharge({ isBPT });
      }
    }
  };

  const hasFormContent =
    (requestType === REQUEST_TYPE_ENUM.CONSTRUCTION && bptTransferType) ||
    (requestType === REQUEST_TYPE_ENUM.AERIAL && aerialTransferType);

  return (
    <div className='flex gap-6 w-full'>
      {/* left section*/}
      <div className={`${hasFormContent ? 'flex-1' : 'w-1/2'} bg-white rounded-2xl shadow-md p-6 h-[260px]`}>
        <div className='border-b border-gray-200 pb-2'>
          <Typography variant='h6-medium'>Accounts/Copy Transfer</Typography>
        </div>
        {(errorMessage || successMessage) && (
          <div className={`alert ${errorMessage ? 'alert-danger' : 'alert-success'} mb-3`} role='alert'>
            <Typography variant='body8'>{errorMessage || successMessage}</Typography>
          </div>
        )}
        {requestType && (
          <RequestsCases
            aerialTransferType={aerialTransferType}
            setAerialTransferType={setAerialTransferType}
            bptTransferType={bptTransferType}
            setBptTransferType={setBptTransferType}
            requestType={requestType}
          />
        )}
      </div>

      {/* right section(forms)*/}
      {hasFormContent && (
        <div className='flex-1 bg-white rounded-2xl shadow-md p-6'>
          {requestType === REQUEST_TYPE_ENUM.CONSTRUCTION && bptTransferType ? (
            <ConstructionInputs
              requestIds={requestIds}
              setRequestIds={setRequestIds}
              handleTransfer={handleTransfer}
              loading={loading}
              bptTransferType={bptTransferType}
              email={email}
              setEmail={setEmail}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              copyTakeoffForCurrentUser={copyTakeoffForCurrentUser}
              setCopyTakeoffForCurrentUser={setCopyTakeoffForCurrentUser}
              isTakeoffFT={isTakeoffFT}
              setIsTakeoffFT={setIsTakeoffFT}
              setShareIds={setShareIds}
              shareIds={shareIds}
            />
          ) : (
            requestType === REQUEST_TYPE_ENUM.AERIAL &&
            aerialTransferType && (
              <>
                <div className='border-b border-gray-200 pb-2 mb-3'>
                  <Typography variant='h6-medium'>
                    {aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE
                      ? 'Move (Original transferred, charge applies to customer)'
                      : aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE
                        ? 'Copy & Transfer (Duplicate created, no charge for customer)'
                        : 'Internal Transfer (Original transferred, no charge)'}
                  </Typography>
                </div>
                {/* for "move" option */}
                {aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE && (
                  <div className='flex gap-4 mb-3'>
                    <div className='flex-1'>
                      <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
                        Type of transfer:
                      </Typography>
                      <TypeOfTransfer transferType={transferType} setTransferType={setTransferType} />
                    </div>
                    {transferType === TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER && (
                      <div className='flex-1'>
                        <Typography variant='body8' className='text-sm font-medium text-gray-700 mb-2'>
                          Is it a FMS to Falcon transfer?:
                        </Typography>
                        <Select
                          fullWidth
                          value={isFmsTransfer ? 'yes' : 'no'}
                          onChange={value => setIsFmsTransfer(value === 'yes')}
                          items={[
                            { label: 'Yes', value: 'yes' },
                            { label: 'No', value: 'no' }
                          ]}
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* form for other options*/}
                <AerialInputs
                  isBPT={isBPT}
                  requestIds={requestIds}
                  setRequestIds={setRequestIds}
                  proceedBtn={false}
                  featureReportId={featureReportId}
                  setFeatureReportId={setFeatureReportId}
                  portfolioName={portfolioName}
                  setPortfolioName={setPortfolioName}
                  perAcreCost={perAcreCost}
                  setPerAcreCost={setPerAcreCost}
                  isFmsTransfer={isFmsTransfer}
                  transferType={transferType}
                  aerialTransferType={aerialTransferType}
                  folderIds={folderIds}
                  setFolderIds={setFolderIds}
                  folderName={folderName}
                  setFolderName={setFolderName}
                  retry={retry}
                  setRetry={setRetry}
                  setRemeasurementRequest={setRemeasurementRequest}
                  remeasurementRequest={remeasurementRequest}
                  email={email}
                  setEmail={setEmail}
                  handleTransfer={handleTransfer}
                  loading={loading}
                  customerEmail={customerEmail}
                  setCustomerEmail={setCustomerEmail}
                />
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};

const RequestsCases = ({
  aerialTransferType,
  setAerialTransferType,
  bptTransferType,
  setBptTransferType,
  requestType
}) => {
  const isBPT = requestType === REQUEST_TYPE_ENUM.CONSTRUCTION;
  const heading = `Select Transfer Type for ${isBPT ? 'Construction' : 'Aerial'} Requests: `;

  const transferValue = isBPT ? bptTransferType : aerialTransferType;
  const setTransferValue = isBPT ? setBptTransferType : setAerialTransferType;
  const NOT_COPY_CHARGE = isBPT
    ? BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE
    : AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE;
  const COPY_NOT_CHARGE = isBPT
    ? BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE
    : AERIAL_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE;
  const NOT_COPY_NOT_CHARGE = isBPT
    ? BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE
    : AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE;

  const radioOptions = [
    {
      label: isBPT
        ? 'Transfer or/and Copy (Original transferred, charge applies to target customer)'
        : 'Move (Original transferred, charge applies to customer)',
      value: NOT_COPY_CHARGE
    },
    {
      label: isBPT
        ? 'Copy (copy created in internal account)'
        : 'Copy & Transfer (Duplicate created, no charge for customer)',
      value: COPY_NOT_CHARGE
    },
    {
      label: 'Internal Transfer (Original transferred, no charge)',
      value: NOT_COPY_NOT_CHARGE
    }
  ];

  return (
    <div className='mb-2'>
      <Typography variant='body3' className='text-left font-medium mb-2'>
        {heading}
      </Typography>
      <RadioGroup
        value={transferValue}
        onChange={(event, newValue) => setTransferValue(Number(newValue))}
        radioGroup={radioOptions}
      />
    </div>
  );
};

const AerialInputs = ({
  requestIds,
  setRequestIds,
  featureReportId,
  setFeatureReportId,
  portfolioName,
  setPortfolioName,
  perAcreCost,
  setPerAcreCost,
  isFmsTransfer,
  transferType,
  aerialTransferType,
  setFolderIds,
  folderIds,
  folderName,
  setFolderName,
  retry,
  setRetry,
  remeasurementRequest,
  setRemeasurementRequest,
  email,
  setEmail,
  handleTransfer,
  loading,
  customerEmail,
  setCustomerEmail
}) => {
  return (
    <div className='mb-3'>
      <IdInputs
        requestIds={requestIds}
        setRequestIds={setRequestIds}
        isFmsTransfer={isFmsTransfer}
        transferType={transferType}
      />
      {aerialTransferType !== AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE &&
        aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE && (
          <div className='mb-3'>
            <Input
              label='Customer email:'
              type='email'
              fullWidth
              value={customerEmail}
              onChange={e => setCustomerEmail(e.target.value)}
              placeholder='Enter customer email address'
              required
            />
          </div>
        )}
      {aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE && (
        <div className='mb-3'>
          <Input
            label='Email Id'
            type='email'
            fullWidth
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder='Enter the customer email id to whom you want to transfer the request'
            required
          />
        </div>
      )}
      <div className='grid grid-cols-2 gap-4 mb-3'>
        {aerialTransferType !== AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE &&
          aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE && (
            <div>
              <Input
                label='Customer email:'
                type='email'
                fullWidth
                value={customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
                placeholder='Enter customer email address'
                required
              />
            </div>
          )}
        {isFmsTransfer && (
          <div>
            <Input
              label='Feature Report Id'
              type='text'
              fullWidth
              value={featureReportId}
              onChange={e => setFeatureReportId(e.target.value)}
              placeholder='Enter feature report id'
              required
            />
          </div>
        )}
        {transferType === TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER &&
          aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE && (
            <>
              <div>
                <Input
                  label='Portfolio name:'
                  type='text'
                  fullWidth
                  value={portfolioName}
                  onChange={e => setPortfolioName(e.target.value)}
                  placeholder='Enter portfolio name'
                  required
                />
              </div>

              <div>
                <Input
                  label='Folder name:'
                  type='text'
                  fullWidth
                  value={folderName}
                  onChange={e => setFolderName(e.target.value)}
                  placeholder='Enter folder name'
                  required
                />
              </div>

              <div>
                <Input
                  label='Per acre cost:'
                  type='text'
                  fullWidth
                  value={perAcreCost}
                  onChange={e => setPerAcreCost(e.target.value)}
                  placeholder='Per acre cost in cents'
                  required
                />
              </div>
            </>
          )}
      </div>
      {aerialTransferType !== AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE && (
        <IdInputs requestIds={folderIds} setRequestIds={setFolderIds} isFolder />
      )}
      {transferType === TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER &&
        aerialTransferType === AERIAL_TANSFER_TYPE_ENUM.NOT_COPY_AND_CHARGE && (
          <div className='mb-3 flex items-center gap-2'>
            <Checkbox
              checked={remeasurementRequest}
              onChange={e => setRemeasurementRequest(e.target.checked)}
              label='Is remeasurement request'
            />
          </div>
        )}
      {isFmsTransfer && (
        <div className='mb-3 flex items-center gap-2'>
          <Checkbox checked={retry} onChange={e => setRetry(e.target.checked)} label='Retry' />
        </div>
      )}
      <div className='flex justify-end mt-3'>
        <Button
          type='submit'
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleTransfer}
          className='!text-base'>
          Transfer aerial requests
          {loading && <LoaderCircle />}
        </Button>
      </div>
    </div>
  );
};

const TypeOfTransfer = ({ transferType, setTransferType }) => {
  return (
    <Select
      fullWidth
      value={String(transferType)}
      onChange={value => setTransferType(Number(value))}
      items={[
        {
          label: TRANSFER_TYPE[TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER],
          value: String(TRANSFER_TYPE_ENUM.PORTFOLIO_TRANSFER)
        },
        { label: TRANSFER_TYPE[TRANSFER_TYPE_ENUM.NON_PORTFOLIO], value: String(TRANSFER_TYPE_ENUM.NON_PORTFOLIO) }
      ]}
    />
  );
};

const IdInputs = ({
  requestIds,
  setRequestIds,
  isBPT,
  isFolder,
  isFmsTransfer,
  transferType,
  setShareIds,
  shareIds
}) => {
  const inputHeading = isBPT
    ? 'Enter or upload Construction Request IDs:'
    : isFolder
      ? 'Folder IDs:'
      : 'Enter or upload Request IDs:';
  const placeholder = `Enter the comma separated automeasure ${
    isBPT
      ? 'construction request'
      : isFolder
        ? 'aerial folder'
        : `aerial${isFmsTransfer ? ' orchestrator:falcon, orchestrator or' : ''} request`
  } ids`;

  const shareIdsPlaceHolder = 'Enter comma separated beam share ids';
  const shareIdsInputHeading = 'Share Ids: ';

  const handleFileInputChange = e => {
    const file = e.target.files[0];

    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const str = reader?.result;
        const lines = str.split(/\r?\n/);

        const firstValues = lines.map(line => {
          const values = line.split(',')[0];
          return values;
        });
        setRequestIds(firstValues);
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className=''>
      <div className={`mb-3 grid ${isFolder ? 'grid-cols-[1fr_3fr]' : 'grid-cols-2'} items-center gap-4`}>
        <Typography variant='body8' className='text-sm font-medium text-gray-700'>
          {inputHeading}
        </Typography>
        <div className='flex gap-0'>
          {/* tooltip to show truncated placeholder */}
          <Tooltip title={!isFolder ? placeholder : ''} placement='top'>
            <div className='w-full'>
              <Input
                type='text'
                fullWidth
                value={requestIds}
                onChange={e => setRequestIds(e.target.value)}
                placeholder={placeholder}
                disabled={shareIds?.length > 0}
                required
                // to truncate the placeholder text
                className='[&_input]:truncate'
              />
            </div>
          </Tooltip>
          {!isFolder && (
            <div className='flex-shrink-0'>
              <input
                type='file'
                id='file-upload'
                className='hidden'
                onChange={handleFileInputChange}
                disabled={shareIds?.length > 0}
                accept='.csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
              />
              <Typography component='label' htmlFor='file-upload' className='cursor-pointer flex items-center h-full'>
                <Button
                  component='span'
                  variant='outlined'
                  color='primary'
                  size='medium'
                  disabled={shareIds?.length > 0}
                  className='text-sm min-w-0 w-16 h-full px-1'>
                  Upload
                </Button>
              </Typography>
            </div>
          )}
        </div>
      </div>
      {!isFolder && isBPT && (
        <>
          <div className='mb-3'>
            <Input
              label={shareIdsInputHeading}
              type='text'
              fullWidth
              value={shareIds}
              onChange={e => setShareIds(e.target.value)}
              placeholder={shareIdsPlaceHolder}
              required
              disabled={requestIds?.length > 0}
            />
          </div>
        </>
      )}
    </div>
  );
};

const ConstructionInputs = ({
  requestIds,
  setRequestIds,
  handleTransfer,
  loading,
  bptTransferType,
  email,
  setEmail,
  setCustomerEmail,
  customerEmail,
  copyTakeoffForCurrentUser,
  setCopyTakeoffForCurrentUser,
  isTakeoffFT,
  setIsTakeoffFT,
  setShareIds,
  shareIds
}) => {
  const [customerEmailError, setCustomerEmailError] = useState('');

  const checkConstructionEmail = email => {
    const validation = validateConstructionEmail(email);
    setCustomerEmailError(validation.valid ? '' : validation.message);
    return validation.valid;
  };

  const validateConstructionEmail = email => {
    if (email === '') {
      return { valid: true, message: '' };
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, message: 'Please enter a valid email address' };
    }

    if (bptTransferType === BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE && !email.endsWith('@attentive.ai')) {
      return { valid: false, message: 'Email must end with @attentive.ai' };
    }

    return { valid: true, message: '' };
  };

  return (
    <div className='mt-3 mx-2'>
      <IdInputs
        requestIds={requestIds}
        setRequestIds={setRequestIds}
        setShareIds={setShareIds}
        isBPT
        shareIds={shareIds}
      />
      <div className='grid grid-cols-2 gap-4 mb-3'>
        {bptTransferType !== BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE &&
          bptTransferType !== BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE && (
            <div>
              <Input
                label='Customer Email'
                type='email'
                fullWidth
                value={customerEmail}
                onChange={e => setCustomerEmail(e.target.value)}
                placeholder='Enter the customer email id to whom you want to transfer the request'
                required
              />
            </div>
          )}

        {bptTransferType === BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE && (
          <div>
            <Input
              label='Internal Email'
              type='email'
              fullWidth
              value={customerEmail}
              onChange={e => {
                checkConstructionEmail(e.target.value);
                setCustomerEmail(e.target.value);
              }}
              placeholder='Enter the internal user email id to whom you want to transfer the request'
              error={!!customerEmailError}
              helperText={customerEmailError}
              required
            />
          </div>
        )}

        {bptTransferType === BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE && (
          <div>
            <Input
              label='Email Id'
              type='email'
              fullWidth
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder='Enter the customer email id to whom you want to transfer the request'
              required
            />
          </div>
        )}
      </div>
      {bptTransferType !== BPT_TANSFER_TYPE_ENUM.NOT_COPY_AND_NOT_CHARGE &&
        bptTransferType !== BPT_TANSFER_TYPE_ENUM.COPY_AND_NOT_CHARGE && (
          <>
            <div className='flex items-center gap-6 mb-3'>
              <Checkbox
                checked={isTakeoffFT}
                onChange={e => setIsTakeoffFT(e.target.checked)}
                label='Mark the takeoff as free trial'
              />
              <Checkbox
                checked={copyTakeoffForCurrentUser}
                onChange={e => setCopyTakeoffForCurrentUser(e.target.checked)}
                label="Copy the takeoff in current user's account"
              />
            </div>
          </>
        )}
      <div className='flex justify-end mt-3'>
        <Button
          type='submit'
          color='primary'
          size='medium'
          variant='contained'
          onClick={handleTransfer}
          className='!text-base'>
          Transfer Construction Requests
          {loading && <LoaderCircle />}
        </Button>
      </div>
    </div>
  );
};

export default CopyTransfer;
